import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ASSOC } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Loader2, CheckCircle2, Sparkles, HeartHandshake, Clock, Wrench } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InscriptionBenevole() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", birth_date: "", skills: "", availability: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Prénom et nom requis"); return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Indiquez au moins un email OU un téléphone"); return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/register-volunteer`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
        <div className="bg-white border border-brand-border rounded-xl shadow-sm p-10 max-w-lg w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-brand-green/10 flex items-center justify-center mb-5">
            <CheckCircle2 className="text-brand-green" size={42} />
          </div>
          <h1 className="font-serif text-3xl text-brand-navy mb-2">Merci pour votre engagement !</h1>
          <p className="text-gray-600 leading-relaxed mb-6">
            Votre candidature de bénévolat a bien été enregistrée. Le bureau de l'association{" "}
            <strong>ACTION IMPACT JEUNESSE</strong> vous recontactera prochainement pour faire connaissance et organiser votre participation.
          </p>
          <Button asChild className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <a href={`https://${ASSOC.website}`} target="_blank" rel="noreferrer">
              Visiter le site de l'association
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="bg-brand-navy text-white">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center gap-4">
          <img src={ASSOC.logo} alt="Logo" className="w-14 h-14 bg-white rounded-full p-1 object-contain" />
          <div>
            <div className="small-caps text-white/60">Devenir bénévole</div>
            <h1 className="font-serif text-3xl leading-tight">Action Impact Jeunesse</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-brand-orange mb-1">
            <Sparkles size={14} />
            <span className="small-caps">S'engager bénévolement</span>
          </div>
          <h2 className="font-serif text-4xl text-brand-navy mb-3">Rejoignez l'équipe des bénévoles</h2>
          <p className="text-gray-600 leading-relaxed">
            En quelques heures par mois ou plus régulièrement, votre énergie peut transformer la vie de jeunes en difficulté.
            Remplissez ce formulaire — nous prendrons contact avec vous rapidement.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white border border-brand-border rounded-lg p-4 flex items-start gap-3">
            <HeartHandshake size={20} className="text-brand-green shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-brand-navy text-sm">Concrètement utile</div>
              <p className="text-xs text-gray-500 mt-0.5">Animation, accompagnement, événements, communication...</p>
            </div>
          </div>
          <div className="bg-white border border-brand-border rounded-lg p-4 flex items-start gap-3">
            <Clock size={20} className="text-brand-orange shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-brand-navy text-sm">À votre rythme</div>
              <p className="text-xs text-gray-500 mt-0.5">Vous choisissez vos disponibilités et vos missions.</p>
            </div>
          </div>
          <div className="bg-white border border-brand-border rounded-lg p-4 flex items-start gap-3">
            <Wrench size={20} className="text-brand-navy shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-brand-navy text-sm">Vos compétences valorisées</div>
              <p className="text-xs text-gray-500 mt-0.5">Attestation de bénévolat délivrée à votre demande.</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="public-volunteer-form" className="bg-white border border-brand-border rounded-xl p-6 md:p-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Prénom *</Label><Input data-testid="public-vol-first-name" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required /></div>
            <div><Label>Nom *</Label><Input data-testid="public-vol-last-name" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="vous@exemple.fr" /></div>
            <div><Label>Téléphone</Label><Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="06 ..." /></div>
            <div className="md:col-span-2"><Label>Adresse postale</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({...form, birth_date: e.target.value})} /></div>
          </div>

          <div>
            <Label>Vos compétences à proposer</Label>
            <Input
              value={form.skills}
              onChange={(e) => setForm({...form, skills: e.target.value})}
              placeholder="Ex : animation, communication, comptabilité, photographie, soutien scolaire..."
            />
          </div>

          <div>
            <Label>Vos disponibilités</Label>
            <Input
              value={form.availability}
              onChange={(e) => setForm({...form, availability: e.target.value})}
              placeholder="Ex : week-ends, soirs en semaine, mercredis après-midi..."
            />
          </div>

          <div>
            <Label>Pourquoi souhaitez-vous être bénévole ? (facultatif)</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Vos motivations, vos expériences, ce qui vous a donné envie..."
            />
          </div>

          <p className="text-xs text-gray-400">
            * Champs obligatoires. Email <em>ou</em> téléphone requis pour que nous puissions vous recontacter.
          </p>

          <Button
            type="submit"
            data-testid="public-volunteer-submit"
            disabled={loading}
            className="w-full h-12 bg-brand-orange hover:bg-[#D04A06] text-white text-base"
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
            Envoyer ma candidature de bénévolat
          </Button>

          <div className="text-center pt-2 border-t border-brand-border">
            <p className="text-xs text-gray-500">
              Vous préférez devenir membre adhérent(e) ?{" "}
              <Link to="/inscription" className="text-brand-orange hover:underline">
                Inscription membre
              </Link>
            </p>
          </div>
        </form>

        <footer className="mt-10 text-center text-xs text-gray-400 leading-relaxed">
          {ASSOC.name} — Association loi 1901<br />
          {ASSOC.address}<br />
          {ASSOC.email} · {ASSOC.website}
        </footer>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
